import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  Self,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  TouchedChangeEvent,
  ValidationErrors,
  Validators,
  ControlEvent,
} from '@angular/forms';
import { SelectOption } from '../model/select-option';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter, map } from 'rxjs';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';

interface ControlData {
  isTouched: boolean;
  isRequired: boolean;
  errors: ValidationErrors | null;
}

@Component({
  selector: 'canvas-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrl: './multi-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MultiSelectComponent
  implements ControlValueAccessor, AfterContentInit
{
  @Input() removeExclamationIcon = false;
  @Input() hideExclamationIcon = true;
  @Input() tooltip: string | null = null;

  @Input() label = '';
  @Input() nullText = 'Select options';
  @Input() options: SelectOption[] = [];

  selectControl = new FormControl<string[]>([]);
  searchControl = new FormControl<string>('');
  filteredOptions: SelectOption[] = [];

  private onTouched = () => {};
  private onChanged = (value: any): void => {};
  private destroyRef = inject(DestroyRef);

  faCircleInfo = faCircleInfo;
  dropdownOpen = false;

  private controlDataSubject = new BehaviorSubject<ControlData>({
    isRequired: false,
    isTouched: false,
    errors: null,
  });
  data$ = this.controlDataSubject.asObservable();

  constructor(@Self() private ngControl: NgControl, private elRef: ElementRef) {
    ngControl.valueAccessor = this;

    this.selectControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.onChanged(value ?? []));

    this.selectControl.events
      .pipe(
        filter((event) => event instanceof TouchedChangeEvent),
        filter((x) => x.touched),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.onTouched());

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((search) => {
        const term = (search ?? '').toLowerCase();
        this.filteredOptions = this.options.filter((o) =>
          o.text.toLowerCase().includes(term)
        );
      });
  }

  ngAfterContentInit(): void {
    this.filteredOptions = this.options;

    const control = this.ngControl?.control;
    if (control) {
      this.controlDataSubject.next({
        ...this.controlDataSubject.value,
        isRequired: control.hasValidator(Validators.required),
      });

      control.events
        .pipe(
          map<ControlEvent, ControlData>((event) => {
            let { isTouched } = this.controlDataSubject.value;
            if (event instanceof TouchedChangeEvent) {
              isTouched = event.touched;
            }
            return {
              isRequired: control.hasValidator(Validators.required),
              isTouched,
              errors: control.errors,
            };
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((data) => this.controlDataSubject.next(data));
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elRef.nativeElement.contains(event.target);
    if (!clickedInside && this.dropdownOpen) {
      this.dropdownOpen = false;
    }
  }

  toggleOption(option: SelectOption) {
    const current = this.selectControl.value ?? [];
    const exists = current.includes(option.id);
    const updated = exists
      ? current.filter((id) => id !== option.id)
      : [...current, option.id];
    this.selectControl.setValue(updated);
    this.selectControl.markAsTouched();
  }

  isSelected(option: SelectOption): boolean {
    return (this.selectControl.value ?? []).includes(option.id);
  }

  writeValue(value: string[]) {
    this.selectControl.setValue(value ?? []);
  }

  registerOnChange(fn: any) {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    isDisabled ? this.selectControl.disable() : this.selectControl.enable();
  }

  getSelectedTexts(): string[] {
    return (this.selectControl.value ?? [])
      .map((id) => this.options.find((o) => o.id === id)?.text)
      .filter((text): text is string => !!text);
  }

  toggleDropdown() {
    if (this.selectControl.disabled) {
      return;
    }

    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.filteredOptions = this.options;
      this.searchControl.setValue('');
    }
  }
}
